import { motion } from "framer-motion";

/**
 * SmithersAvatar — compact holographic geometric figure for the presence panel.
 * Pure CSS+SVG (no R3F dependency — keeps GPU budget for hero scene).
 * - Octahedron silhouette + 2 concentric rotating rings
 * - Cyan glow + ambient halo particles
 * - Subtle scale-pulse + slow rotation
 *
 * `mode`: "present" | "prerecorded" | "offline"
 * `size`: px square — default 168
 */
export default function SmithersAvatar({ mode = "present", size = 168 }) {
    const isPresent = mode === "present";
    return (
        <div
            data-testid="smithers-avatar"
            data-mode={mode}
            className="relative"
            style={{ width: size, height: size }}
            aria-hidden="true"
        >
            {/* Outer rotating ring (slow CW) */}
            <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ animation: "tca-spin-slow 30s linear infinite" }}
            >
                <svg viewBox="0 0 200 200" className="w-full h-full" style={{ opacity: isPresent ? 1 : 0.55 }}>
                    <circle
                        cx="100" cy="100" r="92"
                        fill="none"
                        stroke={isPresent ? "rgba(0,219,255,0.40)" : "rgba(0,219,255,0.18)"}
                        strokeWidth="0.6" strokeDasharray="2 5"
                    />
                    {[...Array(24)].map((_, i) => {
                        const long = i % 3 === 0;
                        return (
                            <line
                                key={i}
                                x1="100" y1="6" x2="100" y2={long ? 13 : 9}
                                stroke={long ? "rgba(0,219,255,0.7)" : "rgba(0,219,255,0.32)"}
                                strokeWidth="0.7"
                                transform={`rotate(${(i * 360) / 24} 100 100)`}
                            />
                        );
                    })}
                </svg>
            </div>

            {/* Inner ring (faster CCW) */}
            <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ animation: "tca-spin-rev 18s linear infinite" }}
            >
                <svg viewBox="0 0 200 200" className="w-full h-full" style={{ opacity: isPresent ? 1 : 0.5 }}>
                    <circle
                        cx="100" cy="100" r="60"
                        fill="none"
                        stroke={isPresent ? "rgba(0,219,255,0.55)" : "rgba(0,219,255,0.24)"}
                        strokeWidth="0.8"
                    />
                </svg>
            </div>

            {/* Octahedron — geometric intelligence figure */}
            <motion.div
                animate={isPresent ? { scale: [1, 1.04, 1] } : { scale: 1 }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-0 flex items-center justify-center"
            >
                <svg viewBox="0 0 200 200" className="w-full h-full">
                    <defs>
                        <linearGradient id="octFill" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="rgba(0,219,255,0.42)" />
                            <stop offset="100%" stopColor="rgba(0,219,255,0.06)" />
                        </linearGradient>
                        <radialGradient id="octHalo" cx="50%" cy="50%" r="70%">
                            <stop offset="0%" stopColor="rgba(0,219,255,0.30)" />
                            <stop offset="100%" stopColor="rgba(0,219,255,0)" />
                        </radialGradient>
                    </defs>
                    {/* halo */}
                    <circle cx="100" cy="100" r="80" fill="url(#octHalo)" opacity={isPresent ? 0.8 : 0.45} />
                    {/* octahedron face */}
                    <polygon
                        points="100,42 142,100 100,158 58,100"
                        fill="url(#octFill)"
                        stroke={isPresent ? "rgba(110,247,255,0.95)" : "rgba(110,247,255,0.55)"}
                        strokeWidth="1.5"
                        style={{ filter: isPresent ? "drop-shadow(0 0 14px rgba(0,219,255,0.55))" : "none" }}
                    />
                    {/* inner segment lines for depth */}
                    <line x1="100" y1="42" x2="100" y2="158" stroke="rgba(110,247,255,0.40)" strokeWidth="0.8" />
                    <line x1="58" y1="100" x2="142" y2="100" stroke="rgba(110,247,255,0.40)" strokeWidth="0.8" />
                    {/* core */}
                    <circle cx="100" cy="100" r="6" fill="rgba(110,247,255,0.95)" style={{ filter: "drop-shadow(0 0 6px rgba(0,219,255,0.9))" }} />
                </svg>
            </motion.div>

            {/* Floating halo particles */}
            {isPresent && (
                <div className="absolute inset-0 pointer-events-none">
                    {[...Array(6)].map((_, i) => {
                        const a = (i / 6) * Math.PI * 2;
                        const r = 0.42;
                        const x = 50 + Math.cos(a) * 36 * r;
                        const y = 50 + Math.sin(a) * 36 * r;
                        return (
                            <motion.span
                                key={i}
                                animate={{ opacity: [0.2, 0.85, 0.2], scale: [0.7, 1.1, 0.7] }}
                                transition={{ duration: 2.6 + i * 0.4, repeat: Infinity, delay: i * 0.3 }}
                                className="absolute w-1 h-1 rounded-full bg-tca-cyan"
                                style={{ left: `${x}%`, top: `${y}%`, boxShadow: "0 0 6px rgba(0,219,255,0.8)" }}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}
